import { createApp, type App as VueApp } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import VueGtag from 'vue-gtag';

// Import the NFC app components
import NfcApp from '@nfc/App.vue';
import router from '@nfc/router';

// Import i18n messages
import en from '@wision/i18n/locales/en';
import sv from '@wision/i18n/locales/sv';
import da from '@wision/i18n/locales/da';
import de from '@wision/i18n/locales/de';

// Note: CSS is loaded via nuxt.config.ts css array to avoid MIME type errors

let nfcAppInstance: VueApp | null = null;

export default defineNuxtPlugin({
  name: 'nfc-app',
  enforce: 'post', // Run after other plugins
  async setup(nuxtApp) {
    const config = useRuntimeConfig();

    // Get the current locale from Nuxt's i18n
    const supportedLocales = ['en', 'sv', 'da', 'de'] as const;
    type SupportedLocale = typeof supportedLocales[number];
    const nuxtI18n = nuxtApp.$i18n as { locale?: { value?: string } } | undefined;
    const rawLocale = nuxtI18n?.locale?.value;
    const currentLocale: SupportedLocale = supportedLocales.includes(rawLocale as SupportedLocale)
      ? (rawLocale as SupportedLocale)
      : 'en';

    // Wait for Nuxt to be fully ready
    nuxtApp.hook('app:mounted', () => {
      // Find the container
      const container = document.getElementById('nfc-app-container');
      if (!container || nfcAppInstance) {
        return;
      }

      // Get the current URL path for initial routing
      const initialPath = window.location.pathname + window.location.search;

      // Create Pinia store
      const pinia = createPinia();

      // Create the NFC Vue app
      nfcAppInstance = createApp(NfcApp);

      // Create vue-i18n instance for the NFC app
      // This is needed so useI18n() composable works in NFC components
      const i18n = createI18n({
        locale: currentLocale,
        messages: { en, sv, da, de },
        globalInjection: true,
        legacy: false,
      });

      // Install plugins
      nfcAppInstance.use(router);
      nfcAppInstance.use(pinia);
      nfcAppInstance.use(i18n);
      nfcAppInstance.use(
        VueGtag,
        {
          config: {
            id: config.public.ga4 as string,
          },
          bootstrap: false,
        },
        router
      );

      // Mount the NFC app inside the container
      nfcAppInstance.mount(container);

      // Navigate to the initial path
      router.push(initialPath);
    });
  },
});
